import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import { clinicService } from '../../services/clinicService';
import { meetingService, Meeting } from '../../services/meetingService';
import { Task } from '../../types/task';
import { DoctorSummaryModal } from '../modals/DoctorSummaryModal';
import { CMSummaryModal } from '../modals/CMSummaryModal';
import ApproveTitrationMeasurementsDialog from '../titration/ApproveTitrationMeasurementsDialog';
import MedicationMonitoringIssueDialog from '../medicationMonitoring/MedicationMonitoringIssueDialog';
import PatientActionDialogs, { PatientAction } from '../internalTasks/PatientActionDialogs';

interface Options {
  /** Called after an action changes something, so the caller can refresh its task list. */
  onChanged?: () => void;
}

/**
 * Shared handling of a task's `action` — what happens when you click a task type.
 *
 * Owns every action dialog so the Tasks page and the patient Issues popup behave
 * identically; mount `dialogs` once and call `openTask(task)` from the click handler.
 */
export function useTaskActions({ onChanged }: Options = {}) {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();

  const [summaryMeeting, setSummaryMeeting] = useState<Meeting | null>(null);
  const [summaryModalType, setSummaryModalType] = useState<'doctor' | 'cm' | null>(null);
  const [titrationReview, setTitrationReview] = useState<{ patientId: number; patientName: string | null; hmId: string | null } | null>(null);
  const [monitoringReviewPatientId, setMonitoringReviewPatientId] = useState<number | null>(null);
  // "Schedule Earlier Follow-Up" reuses the existing Set Meeting (Yoman) booking flow.
  const [followUpPatientId, setFollowUpPatientId] = useState<number | null>(null);
  const [followUpAction, setFollowUpAction] = useState<PatientAction>(null);
  const [clinicState, setClinicState] = useState<string | null>(null);

  // Clinic state (UK/IL) drives the medication section in the psy-intake summary — without
  // it the summary defaults to the IL medication view instead of UK + ADHD decision.
  useEffect(() => {
    if (!currentTenant?.id) return;
    clinicService.getClinics(currentTenant.id)
      .then((data) => setClinicState(data[0]?.state || null))
      .catch(() => {});
  }, [currentTenant?.id]);

  const openTask = useCallback(async (task: Task) => {
    const action = task.action;
    if (action === 'OPEN_SUMMARY' && task.meeting_id) {
      try {
        const meeting = await meetingService.getMeeting(task.meeting_id);
        const isCM = meeting.meeting_type?.type === 'case_manager_intake' || meeting.meeting_type?.type === 'case_manager_followup';
        setSummaryMeeting(meeting);
        setSummaryModalType(isCM ? 'cm' : 'doctor');
      } catch {
        navigate(`/patients/${task.patient_id}`);
      }
    } else if (action === 'OPEN_MEETING' && task.meeting_id) {
      navigate(`/patients/${task.patient_id}?meeting_id=${task.meeting_id}`);
    } else if (action === 'APPROVE_TITRATION_MEASUREMENTS' && task.patient_id) {
      setTitrationReview({ patientId: task.patient_id, patientName: task.patient_name, hmId: task.hm_id });
    } else if (action === 'MEDICATION_MONITORING_ISSUE' && task.patient_id) {
      setMonitoringReviewPatientId(task.patient_id);
    } else {
      navigate(`/patients/${task.patient_id}`);
    }
  }, [navigate]);

  const closeSummary = () => { setSummaryMeeting(null); setSummaryModalType(null); };

  const dialogs = (
    <>
      {summaryMeeting && summaryModalType === 'doctor' && (
        <DoctorSummaryModal isOpen onClose={closeSummary} meeting={summaryMeeting} clinicState={clinicState} />
      )}
      {summaryMeeting && summaryModalType === 'cm' && (
        <CMSummaryModal isOpen onClose={closeSummary} meeting={summaryMeeting} />
      )}
      <ApproveTitrationMeasurementsDialog
        open={titrationReview !== null}
        patientId={titrationReview?.patientId ?? null}
        patientName={titrationReview?.patientName ?? null}
        hmId={titrationReview?.hmId ?? null}
        onClose={() => setTitrationReview(null)}
        onDone={onChanged}
      />
      <MedicationMonitoringIssueDialog
        open={monitoringReviewPatientId !== null}
        patientId={monitoringReviewPatientId}
        onClose={() => setMonitoringReviewPatientId(null)}
        onScheduleFollowUp={() => {
          setFollowUpPatientId(monitoringReviewPatientId);
          setFollowUpAction('set_meeting');
        }}
        onDone={onChanged}
      />
      {followUpPatientId !== null && (
        <PatientActionDialogs
          patientId={followUpPatientId}
          action={followUpAction}
          summaryMeeting={null}
          onClose={() => { setFollowUpAction(null); setFollowUpPatientId(null); }}
        />
      )}
    </>
  );

  return { openTask, dialogs };
}
